import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { getStytch } from '@/auth/common';
import { useAppSession } from '@/auth/session';

const authenticateImpersonationInput = z.object({
  token: z.string().min(1),
});

export type Impersonation = {
  expiresAt: string;
  impersonatedEmail: string | null;
  impersonatorEmail: string;
};

export const authenticateImpersonationFn = createServerFn({ method: 'POST' })
  .validator((data) => authenticateImpersonationInput.parse(data))
  .handler(async ({ data }): Promise<void> => {
    const stytch = getStytch();
    const response = await stytch.impersonation.authenticate({
      impersonation_token: data.token,
    });
    const { session } = await stytch.sessions.authenticate({
      session_jwt: response.session_jwt,
    });
    const impersonatedFactor = session.authentication_factors.find(
      (factor) => factor.type === 'impersonated',
    );
    const impersonatorEmail =
      impersonatedFactor?.impersonated_factor?.impersonator_email_address;
    const expiresAt = session.expires_at;

    if (!impersonatorEmail || !expiresAt) {
      await stytch.sessions.revoke({ session_jwt: response.session_jwt });
      throw new Error(
        'Stytch impersonation session did not identify the impersonator',
      );
    }

    const impersonation: Impersonation = {
      expiresAt,
      impersonatedEmail:
        response.user.emails.find(({ verified }) => verified)?.email ?? null,
      impersonatorEmail,
    };
    const appSession = await useAppSession();
    await appSession.update({
      impersonation,
      stytchJwt: response.session_jwt,
    });
  });

export const getImpersonationFn = createServerFn({ method: 'GET' }).handler(
  async (): Promise<Impersonation | null> => {
    const session = await useAppSession();

    return session.data?.impersonation ?? null;
  },
);

export const endImpersonationFn = createServerFn({ method: 'POST' }).handler(
  async (): Promise<void> => {
    const session = await useAppSession();
    const stytchJwt = session.data?.stytchJwt;

    try {
      if (stytchJwt) {
        await getStytch().sessions.revoke({ session_jwt: stytchJwt });
      }
    } finally {
      await session.clear();
    }
  },
);
